import { useState, useEffect, useCallback } from "react";
import { logErrorToCloudWatch } from "../utils/rum";

const EMERGENCY_FALLBACK_ERROR = {
	code: "LC-DEFAULT-001",
	title: "Error occurred",
	message: "An unexpected error occurred. Please try again.",
};

const useErrorMessages = () => {
	const [errorMessages, setErrorMessages] = useState({});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [configVersion, setConfigVersion] = useState("1.0.0");

	const loadErrorMessages = useCallback(async () => {
		try {
			setLoading(true);
			const timestamp = new Date().getTime();
			const response = await fetch(`/error-messages.json?v=${timestamp}`);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.json();
			setErrorMessages(data.errors || {});
			setConfigVersion(data.version || "1.0.0");
			setError(null);

			console.log(
				`Error messages loaded - Version: ${data.version || "1.0.0"}`
			);
		} catch (err) {
			console.error("Failed to load error messages:", err);
			setError(err);

			setErrorMessages({
				DEFAULT_ERROR: EMERGENCY_FALLBACK_ERROR,
			});

			logErrorToCloudWatch({
				error_code: EMERGENCY_FALLBACK_ERROR.code,
				message: EMERGENCY_FALLBACK_ERROR.title,
				error_response: `Failed to load error messages: ${err.message}`,
			});
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadErrorMessages();

		const handleConfigReload = () => {
			console.log("Config reload event received, reloading error messages...");
			loadErrorMessages();
		};

		window.addEventListener("config-reload", handleConfigReload);

		return () => {
			window.removeEventListener("config-reload", handleConfigReload);
		};
	}, [loadErrorMessages]);

	const getErrorMessage = useCallback(
		(key, error_response) => {
			const errorData =
				errorMessages[key] ||
				errorMessages.DEFAULT_ERROR ||
				EMERGENCY_FALLBACK_ERROR;
			return {
				...errorData,
				error_key: key,
				error_response: error_response ?? "Something went wrong.",
			};
		},
		[errorMessages]
	);

	const refreshErrorMessages = useCallback(() => {
		loadErrorMessages();
	}, [loadErrorMessages]);

	return {
		errorMessages,
		loading,
		error,
		configVersion,
		getErrorMessage,
		refreshErrorMessages,
	};
};

export default useErrorMessages;
